import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Mail, Bell, Loader2, Send } from 'lucide-react';
import { toast } from '@clareefai/ui';
import { useSendSpecificRewardReminderMutation } from '../../../store/vendorApi';
import type { CommitmentTask } from '@clareefai/types';

interface SendReminderModalProps {
  commitmentId: string;
  promoterName: string | null;
  tasks: CommitmentTask[];
  deadline: string;
  onClose: () => void;
}

const SendReminderModal: React.FC<SendReminderModalProps> = ({
  commitmentId, promoterName, tasks, deadline, onClose,
}) => {
  const { t } = useTranslation();
  const [method, setMethod] = useState<'EMAIL' | 'IN_APP'>('EMAIL');
  const pendingTasks = tasks.filter((task) => !task.is_completed).map((task) => task.type.replace('_', ' ').toLowerCase());
  const defaultMessage = `Hi ${promoterName || 'there'},\n\nThis is a friendly reminder about your pending tasks: ${pendingTasks.join(', ')}.\n\nThe deadline is ${new Date(deadline).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}. Please complete them at your earliest convenience.\n\nThank you!`;
  const [message, setMessage] = useState(defaultMessage);
  const [sendReminder, { isLoading }] = useSendSpecificRewardReminderMutation();

  const handleSend = async () => {
    try {
      await sendReminder({ id: commitmentId, body: { method, message } }).unwrap();
      toast.success(t('rewards.specific.reminder.sentSuccess'));
      onClose();
    } catch (err: any) {
      toast.error(err?.data?.message || t('rewards.specific.reminder.failedToSend'));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-[#4339A0] text-white rounded-t-xl">
          <h3 className="font-semibold">{t('rewards.specific.reminder.title')}</h3>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Method Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('rewards.specific.reminder.sendVia')}</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setMethod('EMAIL')}
                className={`p-3 rounded-lg border-2 transition-all flex items-center gap-2 ${
                  method === 'EMAIL' ? 'border-[#4339A0] bg-[#4339A0]/5' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Mail className={`w-5 h-5 ${method === 'EMAIL' ? 'text-[#4339A0]' : 'text-gray-400'}`} />
                <span className={`text-sm font-medium ${method === 'EMAIL' ? 'text-[#4339A0]' : 'text-gray-600'}`}>{t('rewards.specific.reminder.methodEmail')}</span>
              </button>
              <button
                onClick={() => setMethod('IN_APP')}
                className={`p-3 rounded-lg border-2 transition-all flex items-center gap-2 ${
                  method === 'IN_APP' ? 'border-[#4339A0] bg-[#4339A0]/5' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Bell className={`w-5 h-5 ${method === 'IN_APP' ? 'text-[#4339A0]' : 'text-gray-400'}`} />
                <span className={`text-sm font-medium ${method === 'IN_APP' ? 'text-[#4339A0]' : 'text-gray-600'}`}>{t('rewards.specific.reminder.methodInApp')}</span>
              </button>
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('common.message')}</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#4339A0] focus:border-transparent resize-none"
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{message.length} characters</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-5 border-t border-gray-200">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSend}
            disabled={isLoading || !message.trim()}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#4339A0] hover:bg-[#3d2f8f] rounded-lg transition-colors disabled:opacity-50"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {t('rewards.specific.reminder.sendButton')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendReminderModal;
